// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import {
  DownloadIcon,
  InfoIcon,
  StarIcon,
  StarIconOutline,
} from "src/assets/icons";

// Styles
import {
  getSurfaceTokens,
  getSelectedCardHeaderBackground,
} from "src/styles/mui/muiStylesHelper";

// Types
import { ExternalExamType } from "src/services/exams/types";

// Utils
import { getLocalDate, getStartOfToday } from "src/utils/date/dateHelper";
import { downloadExternalExamFile } from "src/utils/exams/externalExamViewerHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useExternalExamsContext } from "src/contexts/exams/ExternalExamsContext";

// Constants
import { eyeTypeEnums, OCT_TYPE, VISUAL_FIELD_TYPE } from "src/constants/enums";

// Hooks
import { useExternalExamById } from "src/services/exams/hooks/external-exams/useExternalExamById";
import { useUpdateFavoriteExam } from "src/services/exams/hooks/external-exams/useUpdateFavoriteExam";

type SelectionCardType = {
  isDragMode: boolean;
  selectedExam: ExternalExamType;
  isSelected: boolean;
  isFavorited: boolean;
  onToggleSelect: () => void;
  onEnterComparisonScreen: () => void;
};

const SelectionCard = ({
  isDragMode,
  selectedExam,
  isSelected,
  isFavorited,
  onToggleSelect,
  onEnterComparisonScreen,
}: SelectionCardType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { surface, onSurface } = getSurfaceTokens(theme);
  const headerBackground = theme.palette.mode === "dark" ? "#44485E" : surface;
  const selectedHeaderBackground = getSelectedCardHeaderBackground(theme);
  const { clinicInfo } = useClinicContext();
  const {
    selectedPatient,
    actions: { resetManualOrder },
  } = useExternalExamsContext();

  const { data: externalExamData } = useExternalExamById(clinicInfo?.id, {
    patientId: selectedPatient?.id,
    examId: selectedExam?.id,
  });

  const { mutate: updateFavoriteExam } = useUpdateFavoriteExam(
    clinicInfo?.id,
    selectedPatient?.id,
    selectedExam.id
  );

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: selectedExam.id });

  // A native double-click fires two "click" events before "dblclick" --
  // without this, each one toggles selection, so by the time the double-
  // click handler runs, isSelected has already flipped away from what it
  // was when the interaction started. Delay the single-click toggle just
  // long enough to cancel it if a second click (real or part of a double-
  // click) arrives first.
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(clickTimeoutRef.current);
  }, []);

  const handleClick = () => {
    clearTimeout(clickTimeoutRef.current);
    clickTimeoutRef.current = setTimeout(() => {
      onToggleSelect();
    }, 200);
  };

  const handleDoubleClick = () => {
    clearTimeout(clickTimeoutRef.current);
    // Comparison mode is only reachable via a selected card -- selecting
    // is what single-click already does, so this just needs to be true
    // at the time of the double-click.
    if (isSelected) onEnterComparisonScreen();
  };

  const dragStyle = {
    borderRadius: "4px",
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: isDragMode ? "grabbing" : "grab",
  };

  const completionDate = useMemo(
    () =>
      getLocalDate(
        selectedExam?.date ?? getStartOfToday(clinicInfo?.timezone),
        clinicInfo?.timezone
      ),
    [selectedExam?.date, clinicInfo?.timezone]
  );

  const isVisualField = useMemo(
    () => selectedExam?.examType?.includes(VISUAL_FIELD_TYPE),
    [selectedExam?.examType]
  );

  const cardTitle = useMemo(() => {
    if (selectedExam?.examType?.includes(VISUAL_FIELD_TYPE)) {
      return t("Visual Field");
    } else if (selectedExam?.examType?.includes(OCT_TYPE)) {
      return t("OCT");
    } else {
      return t("OPD");
    }
  }, [selectedExam?.examType, t]);

  const eyeLabel = useMemo(() => {
    if (externalExamData?.eye === eyeTypeEnums.LEFT) return "OS";
    if (externalExamData?.eye === eyeTypeEnums.RIGHT) return "OD";
    return "OU";
  }, [externalExamData?.eye]);

  const handleDownload = useCallback(() => {
    if (!externalExamData?.examFileUrl) return;
    downloadExternalExamFile(
      externalExamData.examFileUrl,
      `${cardTitle}-${completionDate}`
    );
  }, [externalExamData?.examFileUrl, cardTitle, completionDate]);

  const infoTooltipContent = useMemo(
    () => (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Typography fontSize="12px">
          {cardTitle} ({eyeLabel})
        </Typography>
        <Typography fontSize="12px">{completionDate}</Typography>
        <Typography fontSize="12px">{selectedExam.sourcePlatform}</Typography>
        {isVisualField && (
          <>
            <Typography fontSize="12px">
              {t("MD")}: {externalExamData?.data?.meanDeviation ?? "N/A"}
            </Typography>
            <Typography fontSize="12px">
              {t("PSD")}:{" "}
              {externalExamData?.data?.patternStandardDeviation ?? "N/A"}
            </Typography>
          </>
        )}
      </Box>
    ),
    [
      t,
      cardTitle,
      eyeLabel,
      completionDate,
      selectedExam.sourcePlatform,
      isVisualField,
      externalExamData?.data?.meanDeviation,
      externalExamData?.data?.patternStandardDeviation,
    ]
  );

  return (
    <Grid
      item
      id={selectedExam.id}
      key={selectedExam.id}
      sx={{
        backgroundColor: surface,
        zIndex: isDragMode ? 10 : 1,
        width: "100%",
      }}
      style={{ ...dragStyle }}
      ref={setNodeRef}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      {...attributes}
      {...listeners}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "4px",
          width: "100%",
          border: isSelected ? "1px solid #E2772E" : "1px solid #5B6D7A50",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
            p: "6px 8px",
            backgroundColor: isSelected
              ? selectedHeaderBackground
              : headerBackground,
            color: onSurface,
            borderBottom: isSelected
              ? "1px solid #E2772E"
              : "1px solid #5B6D7A50",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                p: "2px 4px",
                border: "1px solid",
                borderColor: isSelected
                  ? "#E2772E"
                  : theme.palette.mode === "dark"
                    ? "#FFF"
                    : "#5B6D7A",
                borderRadius: "4px",
              }}
            >
              <Typography fontSize="12px">{eyeLabel}</Typography>
            </Box>
            <Typography fontSize="14px" fontWeight={600} noWrap sx={{}}>
              {cardTitle}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              gap: "12px",
            }}
          >
            {externalExamData?.examFileUrl && (
              <IconButton
                disableRipple
                disableFocusRipple
                aria-label={t("Download exam file")}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDownload();
                }}
                sx={{
                  p: 0,
                  color: isSelected
                    ? "#E2772E"
                    : theme.palette.mode === "dark"
                      ? "#FFF"
                      : "#5B6D7A",
                }}
              >
                <DownloadIcon width="16px" height="16px" />
              </IconButton>
            )}
            <IconButton
              disableRipple
              disableFocusRipple
              onClick={(event) => {
                event.stopPropagation();
                // Favoriting/unfavoriting always drops back into auto-sort
                // mode -- the card should shoot to (or out of) the top of
                // its date group even if a manual drag order was active.
                resetManualOrder();
                updateFavoriteExam(!isFavorited);
              }}
              sx={{
                p: 0,
                color: isSelected
                  ? "#E2772E"
                  : theme.palette.mode === "dark"
                    ? "#FFF"
                    : "#5B6D7A",
              }}
            >
              {isFavorited ? (
                <StarIcon width="16px" height="16px" />
              ) : (
                <StarIconOutline width="16px" height="16px" />
              )}
            </IconButton>
            <Tooltip title={infoTooltipContent}>
              <IconButton
                disableRipple
                disableFocusRipple
                onClick={(event) => event.stopPropagation()}
                sx={{
                  p: 0,
                  color: isSelected
                    ? "#E2772E"
                    : theme.palette.mode === "dark"
                      ? "#FFF"
                      : "#5B6D7A",
                }}
              >
                <InfoIcon width="16px" height="16px" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "175px",
            backgroundColor: "#FFF",
            cursor: "pointer",
          }}
        >
          {selectedExam?.examThumbnailUrl ? (
            <img
              src={selectedExam.examThumbnailUrl}
              alt="external-exam-img"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Typography
              sx={{
                color: "#000",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              {t("No image preview available.")}
            </Typography>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default SelectionCard;
